import { Inject, Injectable } from '@angular/core';
import { Functions, httpsCallable } from '@angular/fire/functions';
import { Observable, from } from 'rxjs';
import { map } from 'rxjs/operators';
import { HUBSPOT_FUNCTIONS_BASE_URL } from './hubspot.tokens';

/**
 * HubSpot Integration Service
 *
 * Provides methods to interact with HubSpot CRM via Firebase Cloud Functions
 *
 * @example
 * ```typescript
 * constructor(private hubspot: HubspotService) {}
 *
 * // Create a contact
 * this.hubspot.createContact({
 *   email: 'john@example.com',
 *   firstname: 'John',
 *   lastname: 'Doe'
 * }).subscribe(result => {
 *   console.log('Contact created:', result.contactId);
 * });
 * ```
 */
@Injectable({
  providedIn: 'root',
})
export class HubspotService {
  private userId: string | null = null;

  constructor(
    private functions: Functions,
    @Inject(HUBSPOT_FUNCTIONS_BASE_URL) private functionsBaseUrl: string,
  ) {}

  /**
   * Set the current user ID for HubSpot operations
   */
  setUserId(userId: string): void {
    this.userId = userId;
  }

  /**
   * Get the OAuth URL to initiate HubSpot authentication
   */
  getOAuthUrl(): string {
    if (!this.userId) {
      throw new Error('User ID not set');
    }
    return `${this.functionsBaseUrl}/hubspotOAuthInitiate?userId=${this.userId}`;
  }

  /**
   * Open HubSpot OAuth in a popup window
   */
  authenticateWithHubSpot(): Promise<void> {
    return new Promise((resolve, reject) => {
      const oauthUrl = this.getOAuthUrl();
      const width = 600;
      const height = 700;
      const left = (window.innerWidth - width) / 2;
      const top = (window.innerHeight - height) / 2;

      const popup = window.open(
        oauthUrl,
        'HubSpot OAuth',
        `width=${width},height=${height},left=${left},top=${top}`,
      );

      if (!popup) {
        reject(new Error('Popup blocked'));
        return;
      }

      // Check if popup is closed
      const timer = setInterval(() => {
        if (popup.closed) {
          clearInterval(timer);
          resolve();
        }
      }, 1000);
    });
  }

  // EMAIL OPERATIONS

  /**
   * Send a welcome email to a new customer using HubSpot
   */
  sendWelcomeEmail(data: {
    email: string;
    firstName?: string;
    lastName?: string;
    displayName?: string;
    uid?: string;
    templateId?: string;
  }): Observable<{
    success: boolean;
    statusId: string;
    sendResult: string;
  }> {
    const sendEmailFn = httpsCallable(
      this.functions,
      'sendHubSpotWelcomeEmail',
    );
    return from(sendEmailFn(data)).pipe(map((result: any) => result.data));
  }

  // CONTACT OPERATIONS

  /**
   * Create a new contact in HubSpot
   */
  createContact(data: {
    email: string;
    firstname?: string;
    lastname?: string;
    phone?: string;
    company?: string;
    [key: string]: any;
  }): Observable<{ success: boolean; contactId: string; data: any }> {
    const createContactFn = httpsCallable(
      this.functions,
      'createHubSpotContact',
    );
    return from(createContactFn({ userId: this.userId, ...data })).pipe(
      map((result: any) => result.data),
    );
  }

  /**
   * Update an existing contact
   */
  updateContact(
    contactId: string,
    properties: { [key: string]: any },
  ): Observable<{ success: boolean; contactId: string; data: any }> {
    const updateContactFn = httpsCallable(
      this.functions,
      'updateHubSpotContact',
    );
    return from(
      updateContactFn({
        userId: this.userId,
        contactId,
        properties,
      }),
    ).pipe(map((result: any) => result.data));
  }

  /**
   * Get a contact by ID or email
   */
  getContact(
    idOrEmail: string,
    searchByEmail = false,
  ): Observable<{ success: boolean; data: any }> {
    const getContactFn = httpsCallable(this.functions, 'getHubSpotContact');
    const params = searchByEmail
      ? { userId: this.userId, email: idOrEmail }
      : { userId: this.userId, contactId: idOrEmail };

    return from(getContactFn(params)).pipe(map((result: any) => result.data));
  }

  /**
   * Delete a contact
   */
  deleteContact(
    contactId: string,
  ): Observable<{ success: boolean; contactId: string }> {
    const deleteContactFn = httpsCallable(
      this.functions,
      'deleteHubSpotContact',
    );
    return from(deleteContactFn({ userId: this.userId, contactId })).pipe(
      map((result: any) => result.data),
    );
  }

  /**
   * List contacts with pagination
   */
  listContacts(
    limit = 100,
    after?: string,
  ): Observable<{ success: boolean; contacts: any[]; paging?: any }> {
    const listContactsFn = httpsCallable(this.functions, 'listHubSpotContacts');
    return from(listContactsFn({ userId: this.userId, limit, after })).pipe(
      map((result: any) => result.data),
    );
  }

  // COMPANY OPERATIONS

  /**
   * Create a new company in HubSpot
   */
  createCompany(data: {
    name: string;
    domain?: string;
    city?: string;
    state?: string;
    [key: string]: any;
  }): Observable<{ success: boolean; companyId: string; data: any }> {
    const createCompanyFn = httpsCallable(
      this.functions,
      'createHubSpotCompany',
    );
    return from(createCompanyFn({ userId: this.userId, ...data })).pipe(
      map((result: any) => result.data),
    );
  }

  /**
   * Update an existing company
   */
  updateCompany(
    companyId: string,
    properties: { [key: string]: any },
  ): Observable<{ success: boolean; companyId: string; data: any }> {
    const updateCompanyFn = httpsCallable(
      this.functions,
      'updateHubSpotCompany',
    );
    return from(
      updateCompanyFn({
        userId: this.userId,
        companyId,
        properties,
      }),
    ).pipe(map((result: any) => result.data));
  }

  /**
   * Get a company by ID
   */
  getCompany(companyId: string): Observable<{ success: boolean; data: any }> {
    const getCompanyFn = httpsCallable(this.functions, 'getHubSpotCompany');
    return from(getCompanyFn({ userId: this.userId, companyId })).pipe(
      map((result: any) => result.data),
    );
  }

  /**
   * Associate a contact with a company
   */
  associateContactWithCompany(
    contactId: string,
    companyId: string,
  ): Observable<{ success: boolean; contactId: string; companyId: string }> {
    const associateFn = httpsCallable(
      this.functions,
      'associateContactWithCompany',
    );
    return from(
      associateFn({
        userId: this.userId,
        contactId,
        companyId,
      }),
    ).pipe(map((result: any) => result.data));
  }

  // DEAL OPERATIONS

  /**
   * Create a new deal in HubSpot
   */
  createDeal(data: {
    dealname: string;
    amount?: number;
    dealstage?: string;
    pipeline?: string;
    closedate?: string;
    [key: string]: any;
  }): Observable<{ success: boolean; dealId: string; data: any }> {
    const createDealFn = httpsCallable(this.functions, 'createHubSpotDeal');
    return from(createDealFn({ userId: this.userId, ...data })).pipe(
      map((result: any) => result.data),
    );
  }

  /**
   * Update an existing deal
   */
  updateDeal(
    dealId: string,
    properties: { [key: string]: any },
  ): Observable<{ success: boolean; dealId: string; data: any }> {
    const updateDealFn = httpsCallable(this.functions, 'updateHubSpotDeal');
    return from(
      updateDealFn({
        userId: this.userId,
        dealId,
        properties,
      }),
    ).pipe(map((result: any) => result.data));
  }

  /**
   * Get a deal by ID
   */
  getDeal(dealId: string): Observable<{ success: boolean; data: any }> {
    const getDealFn = httpsCallable(this.functions, 'getHubSpotDeal');
    
    return from(getDealFn({ userId: this.userId, dealId })).pipe(
      map((result: any) => result.data),
    );
  }

  /**
   * Associate a deal with contacts and companies
   */
  associateDeal(
    dealId: string,
    contactIds: string[] = [],
    companyIds: string[] = [],
  ): Observable<{
    success: boolean;
    dealId: string;
    contactIds: string[];
    companyIds: string[];
  }> {
    const associateDealFn = httpsCallable(this.functions, 'associateDeal');
    return from(
      associateDealFn({
        userId: this.userId,
        dealId,
        contactIds,
        companyIds,
      }),
    ).pipe(map((result: any) => result.data));
  }
}
